import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { TableSkeleton } from '../../Components/common/Skeleton';
import { X, Trash2, RotateCcw, AlertTriangle } from 'lucide-react';
import Pagination from '../../Components/common/Pagination';

const UserList = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    role: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });

  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [deletingBulk, setDeletingBulk] = useState(false);

  // Recycle Bin / Trash States
  const [showTrashModal, setShowTrashModal] = useState(false);
  const [trashUsers, setTrashUsers] = useState([]);
  const [loadingTrash, setLoadingTrash] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  const [purgingId, setPurgingId] = useState(null);

  const [showAddModal, setShowAddModal] = useState(false);
  const [availableRoles, setAvailableRoles] = useState([]);

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const res = await api.get('/roles');
      if (res.data?.data) {
        setAvailableRoles(res.data.data);
      }
    } catch (err) {
      console.error('Failed to load roles in UserList:', err);
    }
  };
  const [newUserData, setNewUserData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: 'customer_care',
    aadhaarCard: null,
    panCard: null,
    avatar: null,
    street: '',
    city: '',
    state: '',
    pincode: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchTrashUsers = async () => {
    setLoadingTrash(true);
    try {
      const res = await api.get('/users/trash/list');
      setTrashUsers(res.data.data || []);
    } catch (err) {
      toast.error('Failed to load Trash Bin');
    } finally {
      setLoadingTrash(false);
    }
  };

  const handleRestoreUser = async (id) => {
    setRestoringId(id);
    try {
      const res = await api.post(`/users/trash/restore/${id}`);
      toast.success(res.data.message || 'User restored successfully');
      fetchTrashUsers();
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to restore user');
    } finally {
      setRestoringId(null);
    }
  };

  const handlePurgeUser = async (id) => {
    if (!window.confirm('Permanently delete this user forever? This action CANNOT be undone.')) return;
    setPurgingId(id);
    try {
      await api.delete(`/users/trash/purge/${id}`);
      toast.success('User permanently deleted');
      fetchTrashUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to purge user');
    } finally {
      setPurgingId(null);
    }
  };

  const handleEmptyTrash = async () => {
    if (!window.confirm('Empty entire Trash Bin? ALL trashed users will be PERMANENTLY DELETED forever!')) return;
    try {
      await api.delete('/users/trash/empty');
      toast.success('Trash Bin emptied successfully');
      fetchTrashUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to empty trash');
    }
  };

  useEffect(() => {
    fetchUsers();
    if (user?.role === 'admin') {
      fetchTrashUsers();
    }
  }, [pagination.page, filters]);

  const fetchUsers = async () => {
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...(filters.role && { role: filters.role }),
        ...(filters.search && { search: filters.search })
      });

      const response = await api.get(`/users?${params}`);
      setUsers(response.data.data.users || []);
      setPagination((prev) => ({
        ...prev,
        total: response.data.data.pagination.total || 0,
        pages: response.data.data.pagination.pages || 0
      }));
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const formData = new FormData();
    formData.append('firstName', newUserData.firstName);
    formData.append('lastName', newUserData.lastName);
    formData.append('email', newUserData.email);
    formData.append('phone', newUserData.phone);
    formData.append('role', newUserData.role);

    // Add address fields
    formData.append('address[street]', newUserData.street);
    formData.append('address[city]', newUserData.city);
    formData.append('address[state]', newUserData.state);
    formData.append('address[pincode]', newUserData.pincode);
    formData.append('address[country]', 'India');

    if (newUserData.aadhaarCard) {
      formData.append('aadhaarCard', newUserData.aadhaarCard);
    }
    if (newUserData.panCard) {
      formData.append('panCard', newUserData.panCard);
    }
    if (newUserData.avatar) {
      formData.append('avatar', newUserData.avatar);
    }

    try {
      await api.post('/auth/register', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('User added and welcome email sent!');
      setShowAddModal(false);
      setNewUserData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        role: 'customer_care',
        aadhaarCard: null,
        panCard: null,
        avatar: null,
        street: '',
        city: '',
        state: '',
        pincode: ''
      });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const getRoleColor = (role) => {
    const colors = {
      admin: 'bg-red-100 text-red-800',
      customer_care: 'bg-blue-100 text-blue-800',
      cc_head: 'bg-indigo-100 text-indigo-800',
      doctor: 'bg-green-100 text-green-800',
      hospital: 'bg-purple-100 text-purple-800',
      marketing_agent: 'bg-yellow-100 text-yellow-800',
      accountant: 'bg-orange-100 text-orange-800'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  const allPageUserIds = users.map((u) => u._id);
  const isAllSelected = users.length > 0 && allPageUserIds.every((id) => selectedUserIds.includes(id));

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedUserIds((prev) => prev.filter((id) => !allPageUserIds.includes(id)));
    } else {
      setSelectedUserIds((prev) => [...new Set([...prev, ...allPageUserIds])]);
    }
  };

  const handleSelectUser = (id) => {
    setSelectedUserIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleBulkDelete = async () => {
    if (selectedUserIds.length === 0) return;
    setDeletingBulk(true);
    try {
      await api.post('/users/bulk-delete', { userIds: selectedUserIds });
      toast.success(`Successfully deleted ${selectedUserIds.length} user(s)!`);
      setSelectedUserIds([]);
      setShowBulkDeleteModal(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete selected users');
    } finally {
      setDeletingBulk(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="h-8 w-32 bg-slate-200 animate-pulse rounded-lg"></div>
        </div>
        <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 p-8">
          <TableSkeleton rows={8} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Users</h1>
        <div className="flex items-center gap-3">
          {user?.role === 'admin' && selectedUserIds.length > 0 && (
            <button
              onClick={() => setShowBulkDeleteModal(true)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-bold transition-all flex items-center gap-2 shadow-sm animate-in fade-in duration-200"
            >
              <Trash2 size={16} />
              Delete Selected ({selectedUserIds.length})
            </button>
          )}
          {user?.role === 'admin' && (
            <>
              <button
                onClick={() => {
                  fetchTrashUsers();
                  setShowTrashModal(true);
                }}
                className="px-4 py-2 bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200 rounded-lg font-bold transition-all flex items-center gap-2 relative shadow-xs"
              >
                <Trash2 size={16} className="text-slate-500" />
                <span>Trash Bin</span>
                {trashUsers.length > 0 && (
                  <span className="bg-red-500 text-white text-[10px] font-black px-1.5 py-0.5 rounded-full ml-0.5">
                    {trashUsers.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setShowAddModal(true)}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-bold transition-all"
              >
                + Add User
              </button>
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className={`grid grid-cols-1 ${user?.role === 'admin' ? 'md:grid-cols-2' : ''} gap-4`}>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder="Search by name or email"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          {user?.role === 'admin' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <select
                name="role"
                value={filters.role}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All Roles</option>
                {availableRoles.length > 0 ? (
                  availableRoles.map((r) => (
                    <option key={r._id || r.role_name} value={r.role_name}>
                      {r.role_name === 'marketing_agent'
                        ? 'Lead Partner'
                        : r.role_name === 'cc_head'
                        ? 'Customer Care Head'
                        : r.role_name.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                    </option>
                  ))
                ) : (
                  <>
                    <option value="admin">Admin</option>
                    <option value="customer_care">Customer Care</option>
                    <option value="cc_head">Customer Care Head</option>
                    <option value="doctor">Doctor</option>
                    <option value="hospital">Hospital</option>
                    <option value="marketing_agent">Lead Partner</option>
                    <option value="accountant">Accountant</option>
                  </>
                )}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Users Content */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {/* Mobile View: Handy Cards */}
        <div className="block lg:hidden divide-y divide-gray-100">
          {users.length > 0 ? (
            users.map((u) => (
              <div key={u._id} className="p-4 space-y-3">
                <div className="flex justify-between items-start">
                  <div className="flex items-start gap-3">
                    {user?.role === 'admin' && (
                      <input
                        type="checkbox"
                        checked={selectedUserIds.includes(u._id)}
                        onChange={() => handleSelectUser(u._id)}
                        className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500 cursor-pointer mt-1"
                      />
                    )}
                    <div className="space-y-0.5">
                      <div className="text-sm font-medium text-gray-900">{u.firstName} {u.lastName}</div>
                      <div className="text-xs text-gray-500">{u.email}</div>
                      <div className="text-[10px] text-gray-400 font-medium">{u.phone}</div>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className={`px-2 py-0.5 text-[10px] rounded-full ${getRoleColor(u.role)}`}>
                      {u.role === 'marketing_agent' ? 'Lead Partner' : u.role.replace('_', ' ')}
                    </span>
                    <span className={`px-2 py-0.5 text-[10px] rounded-full ${u.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {u.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
                <Link
                  to={`/users/${u._id}`}
                  className="block w-full text-center py-2 bg-gray-50 text-primary-600 text-xs font-bold rounded-lg border border-gray-100"
                >
                  View Profile
                </Link>
              </div>
            ))
          ) : (
            <div className="p-10 text-center text-gray-400 italic text-sm">
              No users found
            </div>
          )}
        </div>

        {/* Desktop View: Original Table */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {user?.role === 'admin' && (
                  <th className="w-12 px-4 py-3 text-center">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={handleSelectAll}
                      className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500 cursor-pointer"
                    />
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Phone
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.length > 0 ? (
                users.map((u) => (
                  <tr key={u._id} className={`hover:bg-gray-50 ${selectedUserIds.includes(u._id) ? 'bg-primary-50/40' : ''}`}>
                    {user?.role === 'admin' && (
                      <td className="w-12 px-4 py-4 text-center whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedUserIds.includes(u._id)}
                          onChange={() => handleSelectUser(u._id)}
                          className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500 cursor-pointer"
                        />
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {u.firstName} {u.lastName}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{u.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{u.phone}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${getRoleColor(u.role)}`}>
                        {u.role === 'marketing_agent' ? 'Lead Partner' : u.role.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${u.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}
                      >
                        {u.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <Link to={`/users/${u._id}`} className="text-primary-600 hover:text-primary-900 font-medium">
                        View
                      </Link>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={user?.role === 'admin' ? "7" : "6"} className="px-6 py-4 text-center text-gray-500">
                    No users found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between p-4">
            <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
              Displaying {pagination.total > 0 ? (pagination.page - 1) * pagination.limit + 1 : 0}-{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} records
            </div>
            <div className="overflow-x-auto pb-2 sm:pb-0">
              <Pagination
                currentPage={pagination.page}
                totalPages={pagination.pages}
                onPageChange={handlePageChange}
              />
            </div>
          </div>
        )}
      </div>

      {/* Recycle Bin / Trash Modal */}
      {showTrashModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-6 sm:p-8 w-full max-w-3xl shadow-2xl space-y-6 animate-in zoom-in-95 duration-200 max-h-[85vh] flex flex-col relative">
            <button
              onClick={() => setShowTrashModal(false)}
              className="absolute top-6 right-6 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all"
            >
              <X size={20} />
            </button>

            <div className="flex items-center justify-between border-b border-slate-100 pb-4 pr-10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-50 text-red-600 rounded-2xl flex items-center justify-center font-bold">
                  <Trash2 size={22} />
                </div>
                <div>
                  <h2 className="text-xl font-black text-slate-900">Recycle Bin (Trash)</h2>
                  <p className="text-xs text-slate-500 font-medium">
                    Soft-deleted items are stored for <strong>30 days</strong> before automatic permanent deletion.
                  </p>
                </div>
              </div>
              {trashUsers.length > 0 && (
                <button
                  onClick={handleEmptyTrash}
                  className="px-3 py-1.5 bg-red-100 text-red-700 hover:bg-red-200 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5"
                >
                  <Trash2 size={14} />
                  Empty Trash
                </button>
              )}
            </div>

            <div className="flex-1 overflow-y-auto space-y-3 custom-scrollbar pr-1">
              {loadingTrash ? (
                <div className="p-12 text-center text-slate-400 font-bold text-sm">
                  Loading Trash Bin...
                </div>
              ) : trashUsers.length === 0 ? (
                <div className="p-16 text-center text-slate-400 space-y-2">
                  <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto text-slate-300">
                    <Trash2 size={24} />
                  </div>
                  <p className="font-bold text-sm text-slate-500">Trash Bin is empty</p>
                  <p className="text-xs text-slate-400">Deleted users will appear here for 30 days before permanent deletion.</p>
                </div>
              ) : (
                trashUsers.map((u) => (
                  <div
                    key={u._id}
                    className="p-4 rounded-2xl border border-slate-100 bg-slate-50/50 hover:bg-white hover:shadow-md transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-black text-slate-900 text-sm">{u.firstName} {u.lastName}</span>
                        <span className="px-2 py-0.5 rounded-full bg-slate-200 text-slate-700 text-[10px] font-bold uppercase">
                          {u.role?.replace(/_/g, ' ')}
                        </span>
                      </div>
                      <div className="text-xs text-slate-500">{u.email} • {u.phone || 'N/A'}</div>
                      <div className="flex items-center gap-2 text-[11px] font-bold text-amber-600">
                        <AlertTriangle size={12} />
                        <span>Auto-deletes in <strong>{u.daysRemaining} days</strong></span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 self-end sm:self-center">
                      <button
                        onClick={() => handleRestoreUser(u._id)}
                        disabled={restoringId === u._id}
                        className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shadow-xs"
                      >
                        <RotateCcw size={14} />
                        {restoringId === u._id ? 'Restoring...' : 'Restore'}
                      </button>
                      <button
                        onClick={() => handlePurgeUser(u._id)}
                        disabled={purgingId === u._id}
                        className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shadow-xs"
                      >
                        <Trash2 size={14} />
                        {purgingId === u._id ? 'Purging...' : 'Delete Forever'}
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 sm:p-8 w-full max-w-md shadow-2xl space-y-4 animate-in zoom-in-95 duration-200">
            <div className="flex items-center gap-3 text-red-600 font-black text-xl">
              <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center shrink-0">
                <Trash2 size={24} />
              </div>
              Confirm Bulk Delete
            </div>
            <p className="text-gray-600 text-sm leading-relaxed">
              Are you sure you want to permanently delete <strong>{selectedUserIds.length} selected user(s)</strong>?
              This will permanently delete their user profiles, doctor/hospital records, associated leads, surgeries, appointments, payouts, and activity logs.
            </p>
            <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setShowBulkDeleteModal(false)}
                disabled={deletingBulk}
                className="px-4 py-2 bg-gray-100 text-gray-700 font-bold rounded-lg hover:bg-gray-200 transition-all text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleBulkDelete}
                disabled={deletingBulk}
                className="px-4 py-2 bg-red-600 text-white font-bold rounded-lg hover:bg-red-700 transition-all text-sm flex items-center gap-2"
              >
                {deletingBulk ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Deleting...
                  </>
                ) : (
                  `Yes, Delete ${selectedUserIds.length} Users`
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-hidden">
          <div className="bg-white rounded-2xl p-6 sm:p-8 w-full max-w-lg shadow-2xl animate-in zoom-in-95 duration-300 max-h-[90vh] overflow-y-auto relative">
            <button 
              onClick={() => setShowAddModal(false)}
              className="absolute top-6 right-6 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all"
            >
              <X size={20} strokeWidth={3} />
            </button>
            <h2 className="text-2xl font-black text-gray-900 mb-6 flex items-center gap-3 pr-8">
              <div className="w-10 h-10 bg-primary-50 rounded-xl flex items-center justify-center text-primary-600 shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
              </div>
              Add System User
            </h2>

            <form onSubmit={handleAddUser} className="space-y-5">
              {/* Profile Image (Avatar) Picker */}
              <div className="flex flex-col items-center justify-center pb-4 border-b border-gray-100">
                <div className="relative group">
                  <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-primary-50 bg-gray-100 flex items-center justify-center relative shadow-md">
                    {newUserData.avatar ? (
                      <img
                        src={URL.createObjectURL(newUserData.avatar)}
                        className="w-full h-full object-cover"
                        alt="Avatar Preview"
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center text-gray-400">
                        <svg className="w-8 h-8 mb-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <input
                    type="file"
                    onChange={(e) => setNewUserData({ ...newUserData, avatar: e.target.files[0] })}
                    className="hidden"
                    id="avatar-upload"
                    accept="image/*"
                  />
                  <label
                    htmlFor="avatar-upload"
                    className="absolute bottom-0 right-0 p-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full cursor-pointer shadow-lg hover:scale-110 transition-all border-2 border-white flex items-center justify-center"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </label>
                </div>
                <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest mt-2">Upload Profile Photo</label>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest px-1">First Name</label>
                  <input
                    type="text"
                    required
                    value={newUserData.firstName}
                    onChange={(e) => setNewUserData({ ...newUserData, firstName: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 outline-none transition-all font-bold text-gray-700"
                    placeholder="John"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest px-1">Last Name</label>
                  <input
                    type="text"
                    required
                    value={newUserData.lastName}
                    onChange={(e) => setNewUserData({ ...newUserData, lastName: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 outline-none transition-all font-bold text-gray-700"
                    placeholder="Doe"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest px-1">Email Address</label>
                <input
                  type="email"
                  required
                  value={newUserData.email}
                  onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 outline-none transition-all font-bold text-gray-700"
                  placeholder="john.doe@doxez.com"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest px-1">Phone Number</label>
                <input
                  type="text"
                  required
                  value={newUserData.phone}
                  onChange={(e) => setNewUserData({ ...newUserData, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                  maxLength={10}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 outline-none transition-all font-bold text-gray-700"
                  placeholder="10-digit number"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest px-1">Aadhaar Card</label>
                  <div className="relative">
                    <input
                      type="file"
                      onChange={(e) => setNewUserData({ ...newUserData, aadhaarCard: e.target.files[0] })}
                      className="hidden"
                      id="aadhaar-upload"
                      accept=".pdf,image/*"
                    />
                    <label
                      htmlFor="aadhaar-upload"
                      className={`flex flex-col items-center justify-center w-full px-4 py-3 bg-gray-50 border border-dashed rounded-xl cursor-pointer transition-all hover:bg-gray-100 ${newUserData.aadhaarCard ? 'border-emerald-200' : 'border-gray-200'}`}
                    >
                      <div className="flex flex-col items-center">
                        <svg className={`w-6 h-6 mb-1 ${newUserData.aadhaarCard ? 'text-emerald-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <p className="text-[9px] font-bold text-gray-500 truncate w-full text-center">
                          {newUserData.aadhaarCard ? newUserData.aadhaarCard.name : 'Upload Aadhaar'}
                        </p>
                      </div>
                    </label>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest px-1">PAN Card</label>
                  <div className="relative">
                    <input
                      type="file"
                      onChange={(e) => setNewUserData({ ...newUserData, panCard: e.target.files[0] })}
                      className="hidden"
                      id="pan-upload"
                      accept=".pdf,image/*"
                    />
                    <label
                      htmlFor="pan-upload"
                      className={`flex flex-col items-center justify-center w-full px-4 py-3 bg-gray-50 border border-dashed rounded-xl cursor-pointer transition-all hover:bg-gray-100 ${newUserData.panCard ? 'border-emerald-200' : 'border-gray-200'}`}
                    >
                      <div className="flex flex-col items-center">
                        <svg className={`w-6 h-6 mb-1 ${newUserData.panCard ? 'text-emerald-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <p className="text-[9px] font-bold text-gray-500 truncate w-full text-center">
                          {newUserData.panCard ? newUserData.panCard.name : 'Upload PAN'}
                        </p>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
              {/* Geographic Profile Section */}
              <div className="space-y-4 pt-2 border-t border-gray-50">
                <label className="text-[10px] font-black uppercase text-primary-600 tracking-[0.2em] px-1">Geographic Profile</label>

                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest px-1">Street Address</label>
                  <input
                    type="text"
                    value={newUserData.street}
                    onChange={(e) => setNewUserData({ ...newUserData, street: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 outline-none transition-all font-bold text-gray-700"
                    placeholder="123 Main St"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest px-1">City</label>
                    <input
                      type="text"
                      value={newUserData.city}
                      onChange={(e) => setNewUserData({ ...newUserData, city: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 outline-none transition-all font-bold text-gray-700"
                      placeholder="Bhubaneswar"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest px-1">State</label>
                    <input
                      type="text"
                      value={newUserData.state}
                      onChange={(e) => setNewUserData({ ...newUserData, state: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 outline-none transition-all font-bold text-gray-700"
                      placeholder="Odisha"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest px-1">Pincode</label>
                  <input
                    type="text"
                    value={newUserData.pincode}
                    onChange={(e) => setNewUserData({ ...newUserData, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 outline-none transition-all font-bold text-gray-700"
                    placeholder="751001"
                    maxLength={6}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest px-1">System Role</label>
                <select
                  value={newUserData.role}
                  onChange={(e) => setNewUserData({ ...newUserData, role: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 outline-none transition-all font-bold text-gray-700 appearance-none cursor-pointer"
                >
                  {availableRoles.length > 0 ? (
                    availableRoles.map((r) => (
                      <option key={r._id || r.role_name} value={r.role_name}>
                        {r.role_name === 'marketing_agent'
                          ? 'Lead Partner'
                          : r.role_name === 'cc_head'
                          ? 'Customer Care Head'
                          : r.role_name.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                      </option>
                    ))
                  ) : (
                    <>
                      <option value="admin">Admin</option>
                      <option value="customer_care">Customer Care</option>
                      <option value="cc_head">Customer Care Head</option>
                      <option value="accountant">Accountant</option>
                    </>
                  )}
                </select>
              </div>

              <div className="flex justify-end gap-4 mt-8 pt-4 border-t border-gray-50">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-6 py-3 border border-gray-100 rounded-xl font-bold text-gray-500 hover:bg-gray-50 transition-all text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-8 py-3 bg-primary-600 text-white rounded-xl font-black shadow-lg shadow-primary-500/20 hover:bg-primary-700 transition-all disabled:opacity-50 text-sm uppercase tracking-wider"
                >
                  {submitting ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserList;
